import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface Engineer {
  id: number;
  name: string;
  email: string;
  active: boolean;
}

export function useEngineers() {
  return useQuery<Engineer[]>({
    queryKey: ["engineers"],
    queryFn: async () => {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/engineers`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch engineers");
      return res.json();
    },
  });
}

export function useCreateEngineer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Engineer> & { password?: string }) => {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/engineers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to create engineer");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["engineers"] });
    },
  });
}

export function useUpdateEngineer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Engineer> & { password?: string } }) => {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/engineers/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to update engineer");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["engineers"] });
    },
  });
}
